'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { URL } from '@/config/constants';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/design-system/club-ui/basics/accordion';
import { cn } from '@/lib/utils';
import type {
  MobileNavItem,
  MobileNavSection,
  NavItem,
} from './club-header-nav';
import { ClubQuizBanner } from './club-quiz-banner';

/** メニュー内リンクの共通スタイル */
const menuLinkClass =
  'block py-[6px] text-club-p-small md:text-club-p-small-pc font-normal text-club-gray-700 tracking-[0.96px] leading-[150%] hover:text-club-gray-800';

/** マイページメニュー用アコーディオントリガー */
function PcMyPageAccordionTrigger({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header className="flex items-center">
      <AccordionPrimitive.Trigger
        className={cn(
          'flex flex-1 items-center justify-between gap-club-24 font-medium transition-all hover:no-underline [&[data-state=open]>.arrow-icon]:rotate-180',
          className,
        )}
        {...props}
      >
        {children}
        <Image
          src="/icon/club-icon/arrow-bellow.svg"
          width={8}
          height={6}
          alt=""
          className="arrow-icon h-auto w-2 shrink-0 transition-transform duration-200"
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

/** マイページメニュー用パネル */
export function PcMyPageMenuPanel({
  isLoggedIn,
  isSp,
  onMouseEnter,
  onMouseLeave,
  onNavigate,
}: {
  isLoggedIn: boolean;
  isSp?: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onNavigate?: () => void;
}) {
  const handleLinkClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('a[href]')) return;

    onNavigate?.();
  };

  const panelBaseClass = cn(
    'flex w-full flex-col border-l border-club-gray-200 bg-white shadow-lg',
    isSp
      ? 'relative z-[51] shrink-0 overflow-y-auto'
      : 'fixed right-0 top-0 z-[51] md:min-w-[400px] md:w-max h-[calc(100vh-var(--header-height,94px))] overflow-y-auto',
  );
  const panelStyle = isSp ? undefined : { top: 'var(--header-height, 94px)' };
  if (!isLoggedIn) {
    return (
      <div
        className={panelBaseClass}
        style={panelStyle}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClickCapture={handleLinkClickCapture}
      >
        <nav
          className="pt-[40px] pr-[40px] pb-[159px] pl-[40px]"
          aria-label="マイページメニュー（未ログイン）"
        >
          {/* 新規会員登録・ログイン */}
          <div className="mb-club-24 flex flex-col items-center gap-club-16 rounded-lg bg-club-gray-100 px-club-16 py-club-24">
            <Link
              href={URL.ec.signUp}
              className="flex w-full items-center justify-center rounded-lg bg-club-gray-700 px-club-16 py-club-12 text-club-h4 md:text-club-h4-pc font-medium text-white hover:bg-club-gray-800"
            >
              新規会員登録
            </Link>
            <Link
              href={`${URL.account.guest}?loginCallback=${encodeURIComponent('/account')}`}
              className="text-club-p-small md:text-club-p-small-pc font-normal text-club-gray-700 hover:text-club-gray-800"
            >
              ログイン
            </Link>
          </div>

          {/* サポートリンク */}
          <ul className="flex flex-col gap-[6px] px-club-16">
            <li>
              <Link
                href={URL.hairCareAdviceWithPage('top')}
                className={menuLinkClass}
              >
                髪のためのサポート
              </Link>
            </li>
            <li>
              <Link href={URL.faq} className={menuLinkClass}>
                よくあるご質問
              </Link>
            </li>
            <li>
              <Link href={URL.ec.products.top} className={menuLinkClass}>
                お買い物ガイド
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    );
  }

  return (
    <div
      className={panelBaseClass}
      style={panelStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClickCapture={handleLinkClickCapture}
    >
      <nav
        className="pt-[40px] pr-[40px] pb-[159px] pl-[40px]"
        aria-label="マイページメニュー"
      >
        {/* マイページトップ */}
        <Link
          href={URL.account.top}
          className="mb-club-8 flex items-center gap-club-12 rounded-lg bg-club-gray-100 px-club-12 py-club-12 text-club-h4 md:text-club-h4-pc font-medium text-club-gray-900 tracking-[1.12px] leading-[160%] hover:bg-club-gray-200"
        >
          <Image
            src="/icon/club-icon/person.svg"
            width={20}
            height={20}
            alt=""
            className="h-5 w-5 shrink-0"
          />
          <span>マイページトップ</span>
        </Link>

        {/* マイヘアプラン（アコーディオン） */}
        <Accordion type="single" collapsible className="mb-club-8 border-none">
          <AccordionItem value="hair-plan" className="border-none">
            <PcMyPageAccordionTrigger className="rounded-lg bg-club-gray-100 px-club-12 py-club-12 [&[data-state=open]]:rounded-b-none">
              <span className="flex items-center gap-club-12">
                <Image
                  src="/icon/club-icon/flag.svg"
                  width={20}
                  height={20}
                  alt=""
                  className="h-5 w-5 shrink-0"
                />
                <span className="text-club-h4 md:text-club-h4-pc font-medium text-club-gray-900 tracking-[1.12px] leading-[160%]">
                  マイヘアプラン
                </span>
              </span>
            </PcMyPageAccordionTrigger>
            <AccordionContent className="rounded-b-lg bg-club-gray-100 px-club-12 pb-club-12 pt-0">
              <ul className="flex flex-col gap-[6px] pl-[32px]">
                <li>
                  <Link href={URL.myHairPlan} className={menuLinkClass}>
                    お届け内容・スケジュール
                  </Link>
                </li>
                <li>
                  <Link
                    href={URL.myHairPlanWithTab('hair-karte')}
                    className={menuLinkClass}
                  >
                    髪のプロフィール
                  </Link>
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* ランク・ミッション */}
        <Link
          href={URL.memberRewards}
          className="mb-club-16 flex items-center gap-club-12 rounded-lg bg-club-gray-100 px-club-12 py-club-12 text-club-h4 md:text-club-h4-pc font-medium text-club-gray-900 tracking-[1.12px] leading-[160%] hover:bg-club-gray-200"
        >
          <Image
            src="/icon/club-icon/point.svg"
            width={20}
            height={20}
            alt=""
            className="h-5 w-5 shrink-0"
          />
          <span>ランク・ミッション</span>
        </Link>

        {/* 登録情報設定（アコーディオン） */}
        <Accordion
          type="single"
          collapsible
          className="mt-club-8 border-none px-club-16"
        >
          <AccordionItem value="registration" className="border-none">
            <PcMyPageAccordionTrigger className="rounded-lg px-0 py-club-8">
              <span className="text-club-h4 md:text-club-h4-pc font-medium text-club-gray-900 tracking-[1.12px] leading-[160%]">
                登録情報設定
              </span>
            </PcMyPageAccordionTrigger>
            <AccordionContent className="px-0 pb-club-8 pt-0">
              <ul className="flex flex-col gap-[6px] pl-0">
                <li>
                  <Link href={URL.orderHistory} className={menuLinkClass}>
                    購入履歴
                  </Link>
                </li>
                <li>
                  <Link href={URL.ec.profileEdit} className={menuLinkClass}>
                    会員情報変更
                  </Link>
                </li>
                <li>
                  <Link
                    href={URL.ec.deliveryAddressRegistration}
                    className={menuLinkClass}
                  >
                    お届け先変更/登録
                  </Link>
                </li>
                <li>
                  <Link href={URL.ec.creditCardEdit} className={menuLinkClass}>
                    カード情報変更
                  </Link>
                </li>
                <li>
                  <Link href={URL.ec.passwordEdit} className={menuLinkClass}>
                    パスワード更新
                  </Link>
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* サポートリンク */}
        <ul className="mt-club-24 flex flex-col gap-[6px] px-club-16">
          <li>
            <Link
              href={URL.hairCareAdviceWithPage('top')}
              className={menuLinkClass}
            >
              髪のためのサポート
            </Link>
          </li>
          <li>
            <Link href={URL.faq} className={menuLinkClass}>
              よくあるご質問
            </Link>
          </li>
          <li>
            <Link href={URL.ec.products.top} className={menuLinkClass}>
              お買い物ガイド
            </Link>
          </li>
        </ul>

        {/* ログアウト */}
        <div className="mt-club-16 border-t border-club-gray-200 px-club-16 pt-club-16">
          <Link
            href={URL.logout}
            className="block text-club-h4 md:text-club-h4-pc font-medium text-club-gray-900 tracking-[1.12px] leading-[160%] hover:text-club-gray-800"
          >
            ログアウト
          </Link>
        </div>
      </nav>
    </div>
  );
}

/** SPのハンバーガーメニュー */
export function HamburgerMenu({
  open,
  sections,
  linkItems,
  onNavigate,
}: {
  open: boolean;
  sections: MobileNavSection[];
  linkItems: MobileNavItem[];
  onNavigate: () => void;
}) {
  if (!open) return null;

  const renderMenuLink = (
    item: MobileNavItem | NavItem,
    className: string,
    key: string,
  ) => {
    const target = 'target' in item ? item.target : undefined;
    return (
      <Link
        key={key}
        href={item.href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        className={className}
        onClick={onNavigate}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <div className="md:hidden bg-white px-1 py-8">
      <nav
        className="flex flex-col space-y-2"
        aria-label="メインナビゲーション（SPメニュー）"
      >
        <Accordion type="single" collapsible className="w-full border-none">
          {sections.map((section) => (
            <AccordionItem
              key={section.id}
              value={section.id}
              className="border-none"
            >
              <AccordionTrigger className="px-4 py-2 text-club-h4 font-medium text-club-gray-700 hover:no-underline">
                {section.label}
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-2 pt-2">
                <div className="flex flex-col gap-6">
                  {section.items.map((item) =>
                    renderMenuLink(
                      item,
                      'block text-club-p-small font-normal text-club-gray-700 hover:opacity-80',
                      `${section.id}-${item.label}`,
                    ),
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {linkItems.map((item) =>
          renderMenuLink(
            item,
            'px-4 py-2 text-club-h4 font-medium text-club-gray-700 hover:opacity-80',
            item.label,
          ),
        )}

        <div className="mx-4 my-8 border-t border-club-gray-200" />

        <div className="flex items-center justify-between px-4">
          <span className="text-club-h4 font-medium text-club-gray-700 tracking-[1.12px]">
            SNS
          </span>
          <div className="flex items-center gap-5">
            <a
              href={URL.sns.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-club-gray-600 hover:text-club-gray-800 transition-colors"
              aria-label="Instagram"
            >
              <Image
                src="/icon/club-icon/instagram-02.svg"
                width={24}
                height={24}
                alt=""
                className="h-6 w-6"
              />
            </a>
            <a
              href={URL.sns.line}
              target="_blank"
              rel="noopener noreferrer"
              className="text-club-gray-600 hover:text-club-gray-800 transition-colors"
              aria-label="LINE"
            >
              <Image
                src="/icon/club-icon/line-02.svg"
                width={27}
                height={26}
                alt=""
                className="h-6 w-6 object-contain"
              />
            </a>
            <a
              href={URL.sns.x}
              target="_blank"
              rel="noopener noreferrer"
              className="text-club-gray-600 hover:text-club-gray-800 transition-colors"
              aria-label="X"
            >
              <Image
                src="/images/sns/x/logo-black.png"
                width={24}
                height={24}
                alt=""
                className="h-6 w-6 object-contain"
              />
            </a>
          </div>
        </div>
      </nav>

      <div className="mt-10 px-4 pb-6">
        <ClubQuizBanner onClick={onNavigate} />
      </div>
    </div>
  );
}

/** マイページメニュー（枠のみ・未実装） */
export function MyPageMenu() {
  return null;
}
